import { useEffect, useState } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import SvgLogoSimple from "./SvgLogoSimple";


export interface NavItem {
  label: string;
  target: string;
}

interface HeaderProps {
  links: NavItem[];
  homeSection?: string;
  logo?: React.ReactNode;
  siteName?: string;
}

export default function Header2({
  logo,
  siteName,
  links,
  homeSection = "home",
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(homeSection);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);

        if (visible?.target.id) {
          setActiveSection(visible.target.id);
        }
      },
      {
        threshold: 0.35,
        rootMargin: "-100px 0px -100px 0px",
      }
    );

    links.forEach((link) => {
      const element = document.getElementById(link.target);

      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [links]);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");

    if (hash) {
      const element = document.getElementById(hash);

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      }
    }
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);

    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    window.history.replaceState(
      null,
      "",
      `#${sectionId}`
    );

    setActiveSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6">
        {/* Logo */}
        <button
          type="button"
          onClick={() => scrollToSection(homeSection)}
          className={` flex justify-center items-start gap-2 ${isScrolled ? "text-indigo-800 eng font-extrabold" : "text-amber-100"}  shrink-0 transition-transform duration-200 hover:scale-105"
          aria-label="Go to Home`}
        >
          {logo}
          {siteName}
          {/* <SvgLogoSimple
            name="GA. VILLINILI"
            primaryColor="#10B981"
            secondaryColor="#064E3B"
            width={90}
            height={60}
          /> */}
          {/* <img
            src={isScrolled ? LogoDark : LogoWhite}
            alt="Logo"
            className="h-auto w-[140px] sm:w-[160px]"
          /> */}
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {links.map((item) => {
            const active = activeSection === item.target;

            return (
              <button
                key={item.target}
                type="button"
                onClick={() => scrollToSection(item.target)}
                className={`relative text-[13px] lg:text-[15px] uppercase tracking-[2px] lg:tracking-[3px] font-medium transition-all duration-200 ${
                  active
                    ? "text-blue-600 font-semibold"
                    : isScrolled
                    ? "text-gray-700"
                    : "text-white"
                } hover:text-blue-500`}
              >
                {item.label}

                {active && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-blue-600" />
                )}
              </button>
            );
          })}
        </nav>

        {/* Mobile Menu Button */}
        <button
          type="button"
          onClick={() =>
            setIsMobileMenuOpen((prev) => !prev)
          }
          className="md:hidden text-2xl"
          aria-label={
            isMobileMenuOpen
              ? "Close menu"
              : "Open menu"
          }
        >
          {isMobileMenuOpen ? (
            <FaTimes
              className={
                isScrolled
                  ? "text-gray-800"
                  : "text-white"
              }
            />
          ) : (
            <FaBars
              className={
                isScrolled
                  ? "text-gray-800"
                  : "text-white"
              }
            />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-x-0 top-[72px] bg-white shadow-2xl transition-all duration-300 ${
          isMobileMenuOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
      >
        <nav className="flex flex-col p-6 space-y-4">
          {links.map((item) => {
            const active =
              activeSection === item.target;

            return (
              <button
                key={item.target}
                type="button"
                onClick={() =>
                  scrollToSection(item.target)
                }
                className={`text-left py-2 uppercase tracking-[2px] border-b border-gray-100 transition-colors ${
                  active
                    ? "text-blue-600 border-blue-600"
                    : "text-gray-700 hover:text-blue-600"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </nav>
      </div>
    </header>
  );
}